/**
 * Jurisdiction Context - Global jurisdiction selection
 * Phase 2: Jurisdiction Management
 *
 * Provides global context for currently selected jurisdiction.
 * Selection persists across all admin pages and page refreshes.
 * Mode-aware: Only persists to localStorage in Demo mode.
 */

import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useApp } from "@/contexts/AppContext";
import { DEMO_JURISDICTIONS } from "@/data/demo-data";
import type { CashFlowSettings, Jurisdiction } from "@/types/jurisdiction";

interface JurisdictionContextType {
  selectedJurisdiction: Jurisdiction | null;
  setSelectedJurisdiction: (jurisdiction: Jurisdiction | null) => void;
  jurisdictions: Jurisdiction[];
  setJurisdictions: (jurisdictions: Jurisdiction[]) => void;
  defaultJurisdictionId: string | null;
  setDefaultJurisdictionId: (id: string | null) => void;
  isLoading: boolean;
}

const JurisdictionContext = createContext<JurisdictionContextType | undefined>(
  undefined,
);

// Storage keys
const STORAGE_KEYS = {
  JURISDICTIONS: "cashflow_jurisdictions",
  SETTINGS: "cashflow_settings",
  SELECTED_JURISDICTION_ID: "cashflow_selected_jurisdiction_id",
};

interface JurisdictionProviderProps {
  children: ReactNode;
}

export function JurisdictionProvider({ children }: JurisdictionProviderProps) {
  const { mode } = useApp();
  const [jurisdictions, setJurisdictionsState] = useState<Jurisdiction[]>([]);
  const [selectedJurisdiction, setSelectedJurisdictionState] =
    useState<Jurisdiction | null>(null);
  const [defaultJurisdictionId, setDefaultJurisdictionIdState] = useState<
    string | null
  >(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load jurisdictions and settings from localStorage on mount (Demo mode only)
  // In Live mode, data is loaded by the component via API
  useEffect(() => {
    const loadData = () => {
      // In Live mode, start with empty state - component will load from API
      if (mode === "live") {
        setJurisdictionsState([]);
        setDefaultJurisdictionIdState(null);
        setIsLoading(false);
        return;
      }

      // Demo mode: load from localStorage
      try {
        // Load jurisdictions
        const storedJurisdictions = localStorage.getItem(
          STORAGE_KEYS.JURISDICTIONS,
        );
        let parsedJurisdictions: Jurisdiction[] = storedJurisdictions
          ? JSON.parse(storedJurisdictions)
          : [];

        // If no jurisdictions exist, initialize with demo UK jurisdiction
        if (parsedJurisdictions.length === 0) {
          parsedJurisdictions = DEMO_JURISDICTIONS;
          localStorage.setItem(
            STORAGE_KEYS.JURISDICTIONS,
            JSON.stringify(parsedJurisdictions),
          );
        }

        // Load settings (for default jurisdiction)
        const storedSettings = localStorage.getItem(STORAGE_KEYS.SETTINGS);
        let parsedSettings: CashFlowSettings = storedSettings
          ? JSON.parse(storedSettings)
          : {
              baseCurrency: "GBP",
              multiJurisdictionMode: false,
              defaultJurisdictionId: null,
              lastUpdated: new Date(),
            };

        // If no default jurisdiction is set, try to set UK (GB) as default
        if (
          !parsedSettings.defaultJurisdictionId &&
          parsedJurisdictions.length > 0
        ) {
          const ukJurisdiction = parsedJurisdictions.find(
            (j) => j.countryCode === "GB" && j.isActive,
          );
          if (ukJurisdiction) {
            parsedSettings.defaultJurisdictionId = ukJurisdiction.id;
            // Save the updated settings
            localStorage.setItem(
              STORAGE_KEYS.SETTINGS,
              JSON.stringify(parsedSettings),
            );
          }
        }

        setJurisdictionsState(parsedJurisdictions);
        setDefaultJurisdictionIdState(
          parsedSettings.defaultJurisdictionId || null,
        );

        // Auto-select jurisdiction
        if (parsedJurisdictions.length > 0) {
          // Try to load previously selected jurisdiction
          const storedSelectedId = localStorage.getItem(
            STORAGE_KEYS.SELECTED_JURISDICTION_ID,
          );

          let jurisdictionToSelect: Jurisdiction | null = null;

          if (storedSelectedId) {
            // Use previously selected if it still exists and is active
            jurisdictionToSelect =
              parsedJurisdictions.find(
                (j) => j.id === storedSelectedId && j.isActive,
              ) || null;
          }

          if (!jurisdictionToSelect && parsedSettings.defaultJurisdictionId) {
            // Fall back to default jurisdiction if set
            jurisdictionToSelect =
              parsedJurisdictions.find(
                (j) =>
                  j.id === parsedSettings.defaultJurisdictionId && j.isActive,
              ) || null;
          }

          if (!jurisdictionToSelect) {
            // Fall back to first active jurisdiction
            jurisdictionToSelect =
              parsedJurisdictions.find((j) => j.isActive) || null;
          }

          setSelectedJurisdictionState(jurisdictionToSelect);
        }

        setIsLoading(false);
      } catch (error) {
        console.error("Error loading jurisdiction data:", error);
        setIsLoading(false);
      }
    };

    loadData();
  }, [mode]);

  // Save selected jurisdiction ID to localStorage whenever it changes (Demo mode only)
  useEffect(() => {
    // Only persist to localStorage in Demo mode
    if (mode === "demo") {
      if (selectedJurisdiction) {
        localStorage.setItem(
          STORAGE_KEYS.SELECTED_JURISDICTION_ID,
          selectedJurisdiction.id,
        );
      } else {
        localStorage.removeItem(STORAGE_KEYS.SELECTED_JURISDICTION_ID);
      }
    }
  }, [selectedJurisdiction, mode]);

  // Wrapper to update jurisdictions and sync to localStorage (Demo mode only)
  const setJurisdictions = (newJurisdictions: Jurisdiction[]) => {
    setJurisdictionsState(newJurisdictions);

    // Only persist to localStorage in Demo mode
    if (mode === "demo") {
      localStorage.setItem(
        STORAGE_KEYS.JURISDICTIONS,
        JSON.stringify(newJurisdictions),
      );
    }

    // If selected jurisdiction was deleted or deactivated, clear selection
    if (selectedJurisdiction) {
      const stillExists = newJurisdictions.find(
        (j) => j.id === selectedJurisdiction.id && j.isActive,
      );
      if (!stillExists) {
        const firstActive = newJurisdictions.find((j) => j.isActive);
        setSelectedJurisdictionState(firstActive || null);
      } else {
        // Update the selected jurisdiction with latest data
        setSelectedJurisdictionState(stillExists);
      }
    }
  };

  // Wrapper to update selected jurisdiction
  const setSelectedJurisdiction = (jurisdiction: Jurisdiction | null) => {
    setSelectedJurisdictionState(jurisdiction);
  };

  // Wrapper to update default jurisdiction ID (persists to localStorage in Demo mode only)
  const setDefaultJurisdictionId = (id: string | null) => {
    setDefaultJurisdictionIdState(id);

    // Only persist to localStorage in Demo mode
    if (mode === "demo") {
      const storedSettings = localStorage.getItem(STORAGE_KEYS.SETTINGS);
      const settings: CashFlowSettings = storedSettings
        ? JSON.parse(storedSettings)
        : {
            baseCurrency: "GBP",
            multiJurisdictionMode: false,
            lastUpdated: new Date(),
          };

      const updatedSettings: CashFlowSettings = {
        ...settings,
        defaultJurisdictionId: id,
        lastUpdated: new Date(),
      };

      localStorage.setItem(
        STORAGE_KEYS.SETTINGS,
        JSON.stringify(updatedSettings),
      );
    }
  };

  const value: JurisdictionContextType = {
    selectedJurisdiction,
    setSelectedJurisdiction,
    jurisdictions,
    setJurisdictions,
    defaultJurisdictionId,
    setDefaultJurisdictionId,
    isLoading,
  };

  return (
    <JurisdictionContext.Provider value={value}>
      {children}
    </JurisdictionContext.Provider>
  );
}

// Hook to use jurisdiction context
export function useJurisdiction() {
  const context = useContext(JurisdictionContext);
  if (context === undefined) {
    throw new Error(
      "useJurisdiction must be used within a JurisdictionProvider",
    );
  }
  return context;
}
